import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Eraser, RotateCcw } from 'lucide-react';
import api, { getClientTabId } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { notify } from '@/components/ui/notify';
import { buildUndoConfirm, humanizeUndoLabel, isRedo } from '@/components/entry/undo-text';
import { getErrorMessage } from '@/lib/errors';

/*
  Undo, shell-wide.

  - After any successful change (api.js fires `app:mutation-success` for POST/PUT/PATCH/DELETE) the
    provider asks the backend for the user's newest undoable change. When a NEW one appears it shows ONE
    toast "‹what changed› · Undo · 8s" (components/ui/notify). A plain `toast.success` the page showed
    for the same change is folded into it (its wording is kept); if the page already showed a `notify`
    toast WITH its own Undo for the change, the page owns the feedback and no second toast appears
    (a notify.success without Undo is folded in like a plain toast).
  - There is no permanent Undo button any more: older changes are undone from "Recent changes" in the
    user menu (last 10, with dates), which always confirms first and says exactly what will happen.
  - From the toast, undos that delete a project or ≥ 5 records also confirm first (backend `effect`).
    Clicking Undo then hands over to the dialog (the toast closes rather than showing "Undoing…"
    behind it) and Cancel offers the toast again, so the user does not lose the Undo.
  - Undo targets the exact change (POST /undo/entries/{id}), never "whatever is latest by then", and
    nothing reloads the browser: `undoEpoch` bumps and the Layout remounts the page so it refetches.
  - That remount would silently drop unsaved edits, so before undoing it asks the same guards a real
    reload asks (useUnsavedChanges listens for `beforeunload`); if a page is dirty the undo always
    confirms and the dialog says the edits will be discarded.

  Layout (inside ConfirmProvider) registers useConfirm() via `registerConfirm`.
*/

const UndoContext = createContext(null);
const UNDO_TOAST_PREFIX = 'undo-toast:';
const MUTATION_SETTLE_MS = 450;
// A change older than this is never offered in a toast (clock skew / another tab / an old session).
const FRESH_WINDOW_MS = 10 * 60 * 1000;

const isFresh = (createdAt) => {
  const time = Date.parse(createdAt || '');
  return Number.isFinite(time) && Math.abs(Date.now() - time) <= FRESH_WINDOW_MS;
};

/**
 * True when a page has unsaved edits. Fires a cancelable synthetic `beforeunload` — the event the old
 * full-page reload fired — so every useUnsavedChanges() guard (or any other beforeunload guard) answers.
 * Synthetic events never show the browser's own "Leave site?" prompt.
 */
export const pageHasUnsavedEdits = () => {
  if (typeof window === 'undefined' || typeof Event !== 'function') return false;
  try {
    const probe = new Event('beforeunload', { cancelable: true });
    window.dispatchEvent(probe);
    return probe.defaultPrevented;
  } catch {
    return false;
  }
};

/** Toast undos of small changes run straight away; everything else asks first. */
const needsConfirmation = (action, source) => (
  source !== 'toast' || !action.effect || Boolean(action.effect.requires_confirmation) || pageHasUnsavedEdits()
);

const withUnsavedWarning = (options) => ({
  ...options,
  description: 'This will:',
  consequences: [
    ...(options.consequences?.length
      ? options.consequences
      : [{ icon: RotateCcw, label: 'Put the affected records back the way they were' }]),
    { icon: Eraser, label: 'Discard your unsaved edits on this page' },
  ],
});

const fallbackConfirm = async (options) => {
  const lines = [options.title, options.subtitle, ...(options.consequences || []).map((item) => `• ${item.label || item}`)];
  // eslint-disable-next-line no-alert
  const ok = typeof window !== 'undefined' && window.confirm(lines.filter(Boolean).join('\n'));
  if (!ok) return false;
  if (options.onConfirm) await options.onConfirm();
  return true;
};

export function UndoProvider({ children }) {
  const { user } = useAuth();
  const [latestAction, setLatestAction] = useState(null);
  const [undoing, setUndoing] = useState(false);
  const [undoEpoch, setUndoEpoch] = useState(0);
  const seenIdRef = useRef(null);
  const baselineRef = useRef(Promise.resolve());
  const snapshotRef = useRef(null);
  const undoToastRef = useRef(null);
  const toastSeqRef = useRef(0);
  const confirmRef = useRef(null);
  const userId = user?.id || user?.email || null;

  // `ownTab`: only a change made from this browser tab (the toast after this tab's own mutation).
  const fetchLatest = useCallback(async ({ ownTab = false } = {}) => {
    try {
      const res = await api.get('/undo/latest', ownTab ? { params: { tab: getClientTabId() } } : undefined);
      return res.data?.action || null;
    } catch {
      return null;
    }
  }, []);

  const refreshLatestUndo = useCallback(async () => {
    if (!userId) {
      setLatestAction(null);
      return null;
    }
    const action = await fetchLatest();
    setLatestAction(action);
    return action;
  }, [fetchLatest, userId]);

  // Baseline: whatever is undoable when the session starts is NOT announced as new.
  useEffect(() => {
    seenIdRef.current = null;
    if (!userId) {
      setLatestAction(null);
      baselineRef.current = Promise.resolve();
      return;
    }
    baselineRef.current = refreshLatestUndo().then((action) => {
      if (action?.id) seenIdRef.current = action.id;
    });
  }, [refreshLatestUndo, userId]);

  const registerConfirm = useCallback((confirmFn) => {
    confirmRef.current = confirmFn;
    return () => {
      if (confirmRef.current === confirmFn) confirmRef.current = null;
    };
  }, []);

  /**
   * Undo one specific change. Resolves 'done', 'cancelled' (nothing was sent) or 'failed' (the undo was
   * tried from the dialog, the error was shown there, then the dialog was closed).
   * `source: 'toast'` skips the confirmation for small undos (see needsConfirmation).
   */
  const runUndo = useCallback(async (action, { source = 'menu' } = {}) => {
    if (!action?.id) return 'cancelled';
    // The server marks a change the caller's role may no longer undo (a module now View only, an invoice delete
    // that needs an Admin's approval — BUG-001) `restorable: false` with the reason; it would refuse it with 403.
    if (action.restorable === false) {
      notify.error(action.blocked_reason || 'You can’t undo this change');
      return 'failed';
    }
    const label = humanizeUndoLabel(action.label);
    let failed = false;
    const perform = async () => {
      setUndoing(true);
      try {
        const res = await api.post(`/undo/entries/${encodeURIComponent(action.id)}`);
        const next = res.data?.next_action || null;
        // The "restore" record the undo itself creates is not a new change to announce.
        if (next?.id) seenIdRef.current = next.id;
        setLatestAction(next);
        setUndoEpoch((value) => value + 1);
        return res.data;
      } catch (error) {
        failed = true;
        throw error;
      } finally {
        setUndoing(false);
      }
    };

    if (needsConfirmation(action, source)) {
      const discardsEdits = pageHasUnsavedEdits();
      const options = buildUndoConfirm(action);
      const confirm = confirmRef.current || fallbackConfirm;
      const ok = await confirm({ ...(discardsEdits ? withUnsavedWarning(options) : options), onConfirm: perform });
      if (!ok) return failed ? 'failed' : 'cancelled';
    } else {
      await perform(); // errors reach the toast, which shows them
    }
    notify.info(isRedo(action) ? `Redone: ${label.replace(/^Undid: /, '')}` : `Undone: ${label}`);
    return 'done';
  }, []);

  /** Undo one specific change; resolves true when undone, false when cancelled or blocked. */
  const requestUndo = useCallback(
    async (action, options) => (await runUndo(action, options)) === 'done',
    [runUndo],
  );

  /** The toast "‹what changed› · Undo · 8s" for `action`. */
  const offerUndoToast = useCallback((action, message) => {
    const show = () => {
      if (undoToastRef.current) toast.dismiss(undoToastRef.current);
      toastSeqRef.current += 1;
      const id = `${UNDO_TOAST_PREFIX}${action.id}:${toastSeqRef.current}`;
      undoToastRef.current = id;
      const forget = () => { if (undoToastRef.current === id) undoToastRef.current = null; };
      notify.success(message, {
        id,
        undo: () => {
          // Small undo: runs now; the toast shows "Undoing…" while the request is in flight.
          if (!needsConfirmation(action, 'toast')) return requestUndo(action, { source: 'toast' });
          // Needs a confirmation: hand over to the dialog. The toast closes now (instead of saying
          // "Undoing…" behind the dialog) and comes back with a fresh 8 s if the user cancels —
          // not when the undo was tried and refused (the dialog showed why).
          forget();
          runUndo(action, { source: 'toast' })
            .then((result) => {
              if (result === 'cancelled' && !undoToastRef.current) show();
            })
            .catch((error) => notify.error(getErrorMessage(error, 'Could not undo the change')));
          return undefined;
        },
        undoneMessage: false,
        onDismiss: forget,
        onAutoClose: forget,
      });
    };
    show();
  }, [requestUndo, runUndo]);

  const showUndoToast = useCallback((action, snapshot) => {
    const fresh = toast.getToasts().filter((item) => (
      !snapshot.has(item.id) && !String(item.id).startsWith(UNDO_TOAST_PREFIX)
    ));
    // notify() toasts are sonner custom toasts whose `jsx` is our <NotifyToast> element.
    const notifyProps = (item) => (item.jsx && item.jsx.props) || null;
    // The page already offered its own Undo for this change: it owns the feedback.
    if (fresh.some((item) => notifyProps(item)?.undo)) return;
    // A plain success message the page showed for the change is folded into the Undo toast.
    const foldable = fresh.filter((item) => item.type === 'success' || notifyProps(item)?.variant === 'success');
    const messageOf = (item) => (notifyProps(item) ? notifyProps(item).message : item.title);
    const pageMessage = foldable.length === 1 && typeof messageOf(foldable[0]) === 'string'
      ? messageOf(foldable[0])
      : null;
    foldable.forEach((item) => toast.dismiss(item.id));
    offerUndoToast(action, pageMessage || humanizeUndoLabel(action.label));
  }, [offerUndoToast]);

  useEffect(() => {
    if (!userId) return undefined;
    let timer = null;
    let cancelled = false;
    const handleMutationSuccess = () => {
      if (!snapshotRef.current) {
        snapshotRef.current = new Set(toast.getToasts().map((item) => item.id));
      }
      clearTimeout(timer);
      timer = setTimeout(async () => {
        const snapshot = snapshotRef.current || new Set();
        snapshotRef.current = null;
        await baselineRef.current;
        // The change THIS tab just made — another tab or session on the same account may have made a newer one.
        const action = await fetchLatest({ ownTab: true });
        if (cancelled) return;
        if (action) setLatestAction(action);
        if (!action?.id || action.id === seenIdRef.current) return;
        seenIdRef.current = action.id;
        // No Undo toast for a change the server says this role may not undo (`restorable: false`, BUG-001): the
        // page's own message stays as it is, and Recent changes shows why.
        if (action.restorable === false || !isFresh(action.created_at)) return;
        // "Restore …" records are written by an undo / admin restore itself, never by an edit.
        if (/^restore\s/i.test(String(action.label || ''))) return;
        showUndoToast(action, snapshot);
      }, MUTATION_SETTLE_MS);
    };

    window.addEventListener('app:mutation-success', handleMutationSuccess);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      window.removeEventListener('app:mutation-success', handleMutationSuccess);
    };
  }, [fetchLatest, showUndoToast, userId]);

  const fetchRecent = useCallback(async (limit = 10) => {
    const res = await api.get('/undo/recent', { params: { limit } });
    return res.data?.items || [];
  }, []);

  // Kept for existing callers: undo the newest change, with the same confirmation as the menu.
  const undoLatest = useCallback(async () => {
    if (!latestAction || undoing) return false; // a not-restorable one is refused (with its reason) in runUndo
    return requestUndo(latestAction, { source: 'menu' });
  }, [latestAction, requestUndo, undoing]);

  const value = useMemo(() => ({
    latestAction,
    refreshLatestUndo,
    undoLatest,
    undoing,
    undoEpoch,
    requestUndo,
    fetchRecent,
    registerConfirm,
  }), [latestAction, refreshLatestUndo, undoLatest, undoing, undoEpoch, requestUndo, fetchRecent, registerConfirm]);

  return (
    <UndoContext.Provider value={value}>
      {children}
    </UndoContext.Provider>
  );
}

export const useUndo = () => useContext(UndoContext);
